import React, { useState, useEffect } from 'react';
import { Card, Tabs, Input, Button, Table, message, Spin, Row, Col, Statistic } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import axios from 'axios';

const { TabPane } = Tabs;

function PoissonDistribution({ fileId, fileName, setSelectedFile, initialConfig }) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [inputData, setInputData] = useState({
    lambda: initialConfig?.lambda ?? 3.0,
    x: initialConfig?.x ?? 0
  });
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (fileId) {
      retrieveAnalysis();
    }
  }, [fileId]);

  const retrieveAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`/api/retrieve_poisson_distribution/${fileId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setInputData(response.data.input_data || {});
        setResults(response.data.output_data || null);
        if (response.data.output_data) {
          setActiveTab("2");
        }
      }
    } catch (error) {
      console.error("Error retrieving analysis:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to retrieve analysis.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSolve = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        name: fileName || "Untitled Analysis",
        input_data: {
          lambda: Number(inputData.lambda),
          x: Number(inputData.x)
        }
      };

      let response;
      if (fileId) {
        response = await axios.put(`/api/update_poisson_distribution/${fileId}/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`/api/save_poisson_distribution/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data) {
        message.success("Analysis saved successfully!");
        setResults(response.data.output_data || null);

        // Update selectedFile to prevent duplicate saves
        if (!fileId && response.data.id) {
          setSelectedFile(prev => ({
            ...prev,
            id: response.data.id,
            name: response.data.name
          }));
        }

        setActiveTab("2");
      }
    } catch (error) {
      console.error("Error saving analysis:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error(error.response?.data?.error || "Failed to save analysis.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderOutputTable = () => {
    if (!results || !results.table_data) return null;

    const columns = [
      {
        title: 'Value (x)',
        dataIndex: 'x',
        key: 'x',
        align: 'center',
        render: (val) => <strong>{val}</strong>
      },
      {
        title: 'Probability P(X = x)',
        dataIndex: 'pmf',
        key: 'pmf',
        align: 'center',
        render: (val) => val !== undefined ? val.toFixed(4) : '-'
      },
      {
        title: 'Cumulative Probability P(X <= x)',
        dataIndex: 'cdf',
        key: 'cdf',
        align: 'center',
        render: (val) => val !== undefined ? val.toFixed(4) : '-'
      }
    ];

    const dataSource = results.table_data.map((row, index) => ({
      key: index,
      ...row
    }));

    return (
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={{ pageSize: 10 }}
        bordered
        size="small"
      />
    );
  };

  return (
    <Card
      title={`Poisson Distribution: ${fileName || 'Untitled Analysis'}`}
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSolve}
          loading={loading}
        >
          Solve & Save
        </Button>
      }
    >
      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Inputs" key="1">
            <div style={{ maxWidth: 400, margin: '0 auto', padding: '24px 0' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Mean (lambda / λ):</label>
                <Input
                  type="number"
                  name="lambda"
                  value={inputData.lambda}
                  onChange={handleInputChange}
                  min={0.0001}
                  step={0.1}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Value (x):</label>
                <Input
                  type="number"
                  name="x"
                  value={inputData.x}
                  onChange={handleInputChange}
                  min={0}
                />
              </div>
            </div>
          </TabPane>
          <TabPane tab="Results" key="2" disabled={!results}>
            {results && (
              <div style={{ marginTop: 16 }}>
                {results.inputs && (
                  <Row gutter={16} style={{ marginBottom: 24, textAlign: 'center' }}>
                    <Col span={12}>
                      <Card size="small">
                        <Statistic title="Mean (λ)" value={results.inputs.lambda_val} precision={4} />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card size="small">
                        <Statistic title="Selected x" value={results.inputs.x} />
                      </Card>
                    </Col>
                  </Row>
                )}
                <h3 style={{ textAlign: 'center', marginBottom: 16 }}>Distribution Table</h3>
                {renderOutputTable()}
              </div>
            )}
          </TabPane>
        </Tabs>
      </Spin>
    </Card>
  );
}

export default PoissonDistribution;
